import type { Order, Product } from '@trmediaab/zebra-types/trhub';
import type { Prettify } from '@trmediaab/zebra-types/utils';
import { useCallback, useEffect } from 'react';
import { useTheme } from 'styled-components';
import useSWR, { SWRConfig } from 'swr';

import ErrorMessage from './components/ErrorMessage';
import OuterContainer from './components/OuterContainer';
import type {
  InitialStateWithCampaign,
  InitialStateWithRedeem,
  InitialStateWithSku,
} from './state';
import {
  setCheckoutStep,
  setHasProductOptions,
  setOrder,
  useCreateState,
} from './state';
import type { OnAuthenticationHandler } from './types/OnAuthenticationHandler';
import request, { defaultFetcher } from './utils/request';
import { useDomSafeId } from './utils/useDomSafeId';
import { LayoutProvider } from './utils/useLayoutContext';
import { ModalProvider } from './utils/useModalContext';
import CampaignStep from './views/Campaign';
import DoneStep from './views/Done';
import PaymentStep from './views/Payment';
import ProductStep from './views/Product';
import RedeemStep from './views/Redeem';
import UserStep from './views/User';

const noop = () => {};

type CheckoutAppBaseProps = Partial<OnAuthenticationHandler> & {
  containerVariant?: 'narrow' | 'wide' | 'none';
};

export interface CheckoutPropsPurchaseWithSku
  extends CheckoutAppBaseProps,
    InitialStateWithSku {
  orderId?: string | undefined;
}

export interface CheckoutPropsPurchaseWithCampaign
  extends CheckoutAppBaseProps,
    InitialStateWithCampaign {
  orderId?: string | undefined;
}

export type CheckoutPropsPurchase = Prettify<
  CheckoutPropsPurchaseWithSku | CheckoutPropsPurchaseWithCampaign
>;

export type CheckoutPropsRedeemGift = Prettify<
  CheckoutAppBaseProps & InitialStateWithRedeem
>;

export type CheckoutAppProps =
  | CheckoutPropsPurchaseWithSku
  | CheckoutPropsPurchaseWithCampaign
  | CheckoutPropsRedeemGift;

const CheckoutApp = (props: CheckoutAppProps) => {
  const instanceId = useDomSafeId();
  const theme = useTheme();
  const { appMode, step, error } = useCreateState(props);

  const { containerVariant, onAuthentication = noop } = props;

  const campaign = 'campaign' in props ? props.campaign : undefined;
  const sku = 'sku' in props ? props.sku : undefined;
  const orderId = 'orderId' in props ? props.orderId : undefined;

  const { data: product } = useSWR<Product>(
    appMode === 'purchase' && sku != null && orderId == null
      ? `product/${sku}`
      : null,
  );

  if (theme == null) {
    throw new Error(
      'Could not find theme, ensure the component is wrapped in ThemeProvider.',
    );
  }

  const onNext = useCallback(() => {
    switch (step) {
      case 'idle':
        return setCheckoutStep('product');
      case 'product':
        return setCheckoutStep('user');
      case 'user':
        return setCheckoutStep(appMode === 'redeemGift' ? 'done' : 'payment');
      case 'payment':
        return setCheckoutStep('done');
      default:
        return;
    }
  }, [appMode, step]);

  const onPrev = useCallback(() => {
    switch (step) {
      case 'user':
        return setCheckoutStep('product');
      case 'payment':
        return setCheckoutStep('user');
      default:
        return;
    }
  }, [step]);

  // If we fetched a product, check if it allows
  // for a different sku to be selected
  useEffect(() => {
    if (product != null) {
      setHasProductOptions(
        'paymentOptions' in product &&
          Array.isArray(product.paymentOptions) &&
          product.paymentOptions.length > 1,
      );
    }
  }, [product]);

  // Move to next step unless we have a pending order to load
  useEffect(() => {
    if (step === 'idle' && orderId == null) {
      onNext();
    }
  }, [onNext, orderId, step]);

  // When orderId is passed to checkout the user has
  // returned from a 3-D Secure verification
  useEffect(() => {
    if (orderId) {
      // eslint-disable-next-line no-inner-declarations
      async function fetchOrderById() {
        try {
          const order = await request<Order>(`order/${orderId}`);

          setHasProductOptions(
            order.lineItems.some(
              lineItem =>
                lineItem.type === 'product' &&
                'paymentOptions' in lineItem.product &&
                Array.isArray(lineItem.product.paymentOptions) &&
                lineItem.product.paymentOptions?.length > 1,
            ),
          );
          setOrder(order);
          setCheckoutStep('payment');
        } catch (error) {
          console.error(error);
        }
      }

      fetchOrderById();
    }
  }, [orderId]);

  let child = null;

  if (error) {
    child = <ErrorMessage error={error} />;
  } else {
    switch (step) {
      case 'product':
        child =
          appMode === 'redeemGift' ? (
            <RedeemStep onNext={onNext} />
          ) : campaign ? (
            <CampaignStep campaign={campaign} onNext={onNext} />
          ) : sku ? (
            <ProductStep sku={sku} onNext={onNext} />
          ) : (
            <ErrorMessage
              error={
                new Error(
                  'Either a campaign or a product must be provided to the checkout.',
                )
              }
            />
          );
        break;
      case 'user':
        child = (
          <UserStep onNext={onNext} onAuthentication={onAuthentication} />
        );
        break;
      case 'payment':
        child = (
          <PaymentStep
            onNext={onNext}
            onPrev={onPrev}
            onAuthentication={onAuthentication}
          />
        );
        break;
      case 'done':
        child = <DoneStep />;
        break;
      default:
        return null;
    }
  }

  return (
    <SWRConfig
      value={{
        // https://swr.vercel.app/docs/options
        fetcher: defaultFetcher,
        dedupingInterval: 2 * 60 * 1000, // cache for 2 min
      }}
    >
      <ModalProvider value={`checkout-modal-${instanceId}`}>
        <LayoutProvider value={containerVariant}>
          <OuterContainer
            variant={
              appMode === 'redeemGift' && step === 'product'
                ? 'none'
                : containerVariant || 'narrow'
            }
          >
            {child}
          </OuterContainer>
        </LayoutProvider>
      </ModalProvider>
    </SWRConfig>
  );
};

export default CheckoutApp;
